import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";

import { SiteLayout } from "@/components/site/SiteLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { adminLogin } from "@/lib/admin-login.functions";
import { SITE_URL } from "@/lib/site";

export const Route = createFileRoute("/partner/login")({
  head: () => ({
    meta: [
      { title: "Inloggen partnerportaal | Taxiritje.nl" },
      {
        name: "description",
        content:
          "Log in op het zakelijke partnerportaal van Taxiritje.nl om ritten te boeken, uw klanten te bekijken en uw commissie te volgen.",
      },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Inloggen partnerportaal | Taxiritje.nl" },
      {
        property: "og:description",
        content: "Toegang voor zakelijke partners van Taxiritje.nl.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/partner/login` }],
  }),
  component: PartnerLoginPage,
});

function PartnerLoginPage() {
  const navigate = useNavigate();
  const tryAdmin = useServerFn(adminLogin);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Beheerder kan ook inloggen op het partnerportaal met gebruikersnaam/wachtwoord.
      const trimmed = email.trim();
      const isAdminAttempt = !trimmed.includes("@");
      if (isAdminAttempt) {
        const result = await tryAdmin({ data: { username: trimmed, password } });
        if (result.ok) {
          try {
            await supabase.auth.signOut({ scope: "local" });
          } catch {
            // ignore
          }
          const { error } = await supabase.auth.signInWithPassword({
            email: result.email,
            password: result.password,
          });
          if (error) throw error;
          await navigate({ to: "/partner" });
          return;
        }
      }

      const { error } = await supabase.auth.signInWithPassword({
        email: trimmed.toLowerCase(),
        password,
      });
      if (error) {
        toast.error("Inloggen is niet gelukt. Controleer uw e-mailadres en wachtwoord.");
        return;
      }
      await navigate({ to: "/partner" });
    } catch {
      toast.error("Inloggen is niet gelukt. Controleer uw gegevens.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <SiteLayout>
      <div className="mx-auto max-w-md px-4 py-12">
        <div className="card-elevated p-6">
          <h1 className="font-display text-2xl font-bold">Inloggen partnerportaal</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Voor reisbureaus, hotels en andere zakelijke partners van Taxiritje.nl.
          </p>
          <form className="mt-5 space-y-4" onSubmit={submit}>
            <div>
              <Label className="text-sm font-medium">E-mailadres</Label>
              <Input
                type="text"
                required
                className="mt-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Wachtwoord</Label>
              <Input
                type="password"
                required
                className="mt-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button type="submit" className="w-full font-semibold" disabled={busy}>
              {busy ? "Inloggen…" : "Inloggen"}
            </Button>
          </form>
          <p className="mt-3 text-sm">
            <Link to="/wachtwoord-vergeten" className="font-semibold underline">
              Wachtwoord vergeten?
            </Link>
          </p>
          <p className="mt-4 text-sm text-muted-foreground">
            Nog geen partneraccount?{" "}
            <Link to="/b2b" className="font-semibold underline">
              Meld uw bedrijf aan
            </Link>
          </p>
        </div>
      </div>
    </SiteLayout>
  );
}
